import React, { useState, useEffect } from 'react';
import userService from '../../services/userService';
import toastService from '../../services/toastService';
import { formatRole } from '../../utils/auth';
import { useTheme } from '../../contexts/ThemeContext';
import styles from './Profile.module.css';

interface CurrentUser {
  id: string;
  name: string;
  role: string;
  email: string;
  isAdmin: boolean;
  permissions: string[];
  vendorId?: string | null;
}

interface FieldErrors {
  name?: string;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
}

const Profile: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const [user, setUser] = useState<CurrentUser | null>(null);

  const [detailsForm, setDetailsForm] = useState({ name: '', email: '' });
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsErrors, setDetailsErrors] = useState<FieldErrors>({});

  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwErrors, setPwErrors] = useState<FieldErrors>({});

  useEffect(() => {
    const stored = localStorage.getItem('oni_current_user');
    if (stored) {
      try {
        const parsed: CurrentUser = JSON.parse(stored);
        setUser(parsed);
        setDetailsForm({ name: parsed.name, email: parsed.email || '' });
      } catch {
        // ignore
      }
    }
  }, []);

  const handleDetailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDetailsForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setDetailsErrors((prev) => ({ ...prev, name: undefined }));
  };

  const handleDetailsSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    if (!detailsForm.name.trim()) {
      setDetailsErrors({ name: 'Name is required.' });
      return;
    }

    setDetailsLoading(true);
    setDetailsErrors({});
    try {
      const updated = await userService.update(user.id, {
        name: detailsForm.name.trim(),
        email: detailsForm.email.trim(),
      });
      const updatedUser = { ...user, name: updated.name, email: updated.email || '' };
      setUser(updatedUser);
      localStorage.setItem('oni_current_user', JSON.stringify(updatedUser));
      toastService.success('Profile updated successfully.');
    } catch (err) {
      toastService.error(err instanceof Error ? err.message : 'Failed to update profile.');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handlePwChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPwForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setPwErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
  };

  const handlePwSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    const errors: FieldErrors = {};
    if (!pwForm.currentPassword) errors.currentPassword = 'Current password is required.';
    if (pwForm.newPassword.length < 8) errors.newPassword = 'New password must be at least 8 characters.';
    if (pwForm.newPassword !== pwForm.confirmPassword) errors.confirmPassword = 'Passwords do not match.';

    if (Object.keys(errors).length > 0) {
      setPwErrors(errors);
      return;
    }

    setPwLoading(true);
    setPwErrors({});
    try {
      await userService.update(user.id, { password: pwForm.newPassword });
      toastService.success('Password changed successfully.');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toastService.error(err instanceof Error ? err.message : 'Failed to change password.');
    } finally {
      setPwLoading(false);
    }
  };

  if (!user) {
    return (
      <div className={styles.container}>
        <p className={styles.noUser}>Not logged in.</p>
      </div>
    );
  }

  const initials = user.name
    .split(' ')
    .map((p) => p[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>My Profile</h1>
        <p className={styles.subtitle}>Manage your account details</p>
      </div>

      <div className={styles.grid}>
        <div className={styles.card}>
          <div className={styles.avatarWrap}>
            <div className={styles.avatar}>{initials}</div>
            <div>
              <p className={styles.userName}>{user.name}</p>
              <span className={styles.roleBadge}>{formatRole(user.role)}</span>
            </div>
          </div>

          <div className={styles.infoList}>
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Role</span>
              <span className={styles.infoValue}>{formatRole(user.role)}</span>
            </div>
            {user.vendorId && (
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Vendor ID</span>
                <span className={styles.infoValue}>{user.vendorId}</span>
              </div>
            )}
            <div className={styles.infoItem}>
              <span className={styles.infoLabel}>Permissions</span>
              <span className={styles.infoValue}>
                {user.permissions.length > 0 ? user.permissions.join(', ') : 'All (admin)'}
              </span>
            </div>
          </div>
        </div>

        <div className={styles.forms}>
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Account Details</h2>
            <form onSubmit={handleDetailsSave} className={styles.form}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="profile-name">Full Name</label>
                <input
                  id="profile-name"
                  name="name"
                  type="text"
                  className={styles.input}
                  value={detailsForm.name}
                  onChange={handleDetailsChange}
                  autoComplete="name"
                />
                {detailsErrors.name && <p className={styles.fieldError}>{detailsErrors.name}</p>}
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="profile-email">Email</label>
                <input
                  id="profile-email"
                  name="email"
                  type="email"
                  className={styles.input}
                  value={detailsForm.email}
                  onChange={handleDetailsChange}
                  autoComplete="email"
                />
              </div>
              <button type="submit" className={styles.saveButton} disabled={detailsLoading}>
                {detailsLoading ? 'Saving…' : 'Save Changes'}
              </button>
            </form>
          </div>

          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Change Password</h2>
            <form onSubmit={handlePwSave} className={styles.form}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="profile-current-pw">Current Password</label>
                <input
                  id="profile-current-pw"
                  name="currentPassword"
                  type="password"
                  className={styles.input}
                  value={pwForm.currentPassword}
                  onChange={handlePwChange}
                  autoComplete="current-password"
                />
                {pwErrors.currentPassword && <p className={styles.fieldError}>{pwErrors.currentPassword}</p>}
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="profile-new-pw">New Password</label>
                <input
                  id="profile-new-pw"
                  name="newPassword"
                  type="password"
                  className={styles.input}
                  value={pwForm.newPassword}
                  onChange={handlePwChange}
                  autoComplete="new-password"
                />
                {pwErrors.newPassword && <p className={styles.fieldError}>{pwErrors.newPassword}</p>}
              </div>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="profile-confirm-pw">Confirm New Password</label>
                <input
                  id="profile-confirm-pw"
                  name="confirmPassword"
                  type="password"
                  className={styles.input}
                  value={pwForm.confirmPassword}
                  onChange={handlePwChange}
                  autoComplete="new-password"
                />
                {pwErrors.confirmPassword && <p className={styles.fieldError}>{pwErrors.confirmPassword}</p>}
              </div>
              <button type="submit" className={styles.saveButton} disabled={pwLoading}>
                {pwLoading ? 'Saving…' : 'Change Password'}
              </button>
            </form>
          </div>

          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Appearance</h2>
            <p className={styles.themeOptionsLabel}>Theme preference</p>
            <div className={styles.themeOptions}>
              <button
                type="button"
                className={`${styles.themeOption} ${theme === 'light' ? styles.themeOptionActive : ''}`}
                onClick={() => setTheme('light')}
                aria-pressed={theme === 'light'}
              >
                <span aria-hidden="true">☀️</span> Light
              </button>
              <button
                type="button"
                className={`${styles.themeOption} ${theme === 'dark' ? styles.themeOptionActive : ''}`}
                onClick={() => setTheme('dark')}
                aria-pressed={theme === 'dark'}
              >
                <span aria-hidden="true">🌙</span> Dark
              </button>
            </div>
            <p className={styles.themeNote}>Your preference is saved automatically.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
